import { MAP_H, MAP_W, STAKE, cellOf, colOf, inBounds } from '../constants';
import type { BoomRngV1 } from '../rng';
import { ORE_GOLD, ORE_IRON, type Terrain } from '../types';

const IRON_BLOBS = 8;
const GOLD_VEINS = 5;
const IRON_LEVELS = 3; // z 0..2
const STAKE_IRON_RADIUS = 12;

function stampIronBlob(terrain: Terrain, rng: BoomRngV1, cx: number, cy: number): void {
  const radius = rng.integer(2, 4);
  for (let dy = -radius; dy <= radius; dy += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) {
      if (dx * dx + dy * dy > radius * radius) continue;
      const x = cx + dx;
      const y = cy + dy;
      if (!inBounds(x, y)) continue;
      const col = colOf(x, y);
      for (let z = 0; z < IRON_LEVELS; z += 1) {
        if (!rng.chance(0.7)) continue;
        const cell = cellOf(col, z);
        terrain.oreType[cell] = ORE_IRON;
        terrain.oreAmount[cell] = rng.integer(10, 30);
      }
    }
  }
}

function stampGoldVein(terrain: Terrain, rng: BoomRngV1): void {
  const z = rng.integer(3, 5);
  let x = rng.integer(4, MAP_W - 5);
  let y = rng.integer(4, MAP_H - 5);
  const length = rng.integer(6, 12);
  for (let index = 0; index < length; index += 1) {
    if (inBounds(x, y)) {
      const cell = cellOf(colOf(x, y), z);
      terrain.oreType[cell] = ORE_GOLD;
      terrain.oreAmount[cell] = rng.integer(2, 6);
    }
    const step = rng.integer(0, 3);
    if (step === 0) x += 1;
    else if (step === 1) x -= 1;
    else if (step === 2) y += 1;
    else y -= 1;
  }
}

/** Flat desert surface; ore seeded by depth. One iron blob is guaranteed near the survey stake. */
export function generateTerrain(terrain: Terrain, rng: BoomRngV1): void {
  terrain.depth.fill(0);
  terrain.oreType.fill(0);
  terrain.oreAmount.fill(0);
  terrain.flags.fill(0);

  // Guaranteed tutorial blob: east of the stake so the starter base never sits on it.
  const nearX = STAKE.x + rng.integer(6, STAKE_IRON_RADIUS);
  const nearY = STAKE.y + rng.integer(-4, 4);
  stampIronBlob(terrain, rng, nearX, nearY);

  for (let blob = 1; blob < IRON_BLOBS; blob += 1) {
    let cx = rng.integer(4, MAP_W - 5);
    let cy = rng.integer(4, MAP_H - 5);
    // Keep the wider economy off the starter doorstep.
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const distance = Math.max(Math.abs(cx - STAKE.x), Math.abs(cy - STAKE.y));
      if (distance >= 10) break;
      cx = rng.integer(4, MAP_W - 5);
      cy = rng.integer(4, MAP_H - 5);
    }
    stampIronBlob(terrain, rng, cx, cy);
  }

  for (let vein = 0; vein < GOLD_VEINS; vein += 1) stampGoldVein(terrain, rng);

  terrain.version += 1;
}
